import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import IcomoonReact from 'icomoon-react';
import Checkbox from '@material-ui/core/Checkbox';

import AttachmentButton from '../components/AttachmentButton';
import Spinner from '../../../shared/_TBComponents/Spinner';
import CreateEditEntryModal from '../../../shared/_TBComponents/CreateEditEntryModal';
import iconSet from '../../../shared/images/teambuildr-selection.json';

import { getImageSource } from '../utils';
import {
  ColorGray,
  ColorPrimary,
  ColorSecondary,
  ColorWhite,
} from '../../../shared/GlobalStyles';

import {
  createMeJournalEntry,
  openImageLightbox,
  openMessageVideo,
  setRemoveMediaArray,
  updateJournalEntry,
  createEntryToAthlete,
} from '../ducks/journalActions';
import { journalTracker } from '../../../shared/utils/amplitudeHelper';

const mediaBoxStyles = (props) => `
  align-items: center;
  background-image: url(${props.backgroundImage});
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  border-radius: 10px;
  display: ${props.isHidden ? 'none' : 'flex'};
  height: 75px;
  justify-content: center;
  margin-right: 16px;
  min-height: 75px;
  min-width: 115px;
  position: relative;
  width: 115px;
  .play-icon {
    background-color: ${ColorGray}73;
    border-radius: 50%;
    cursor: pointer;
    padding: 10px;
  }
  .remove-media {
    background: #d9d9d9;
    border-radius: 50%;
    cursor: pointer;
    padding: 4px;
    pointer-events: stroke;
    position: absolute;
    right: -4px;
    top: -7px;
  }
`;

const EntryErrorText = styled('div')`
  color: ${ColorSecondary};
  font-family: 'Nunito Sans';
  font-size: 16px;
  line-height: 24px;
  margin-bottom: 16px;
`;

const EntryText = styled('textarea')`
  border: none;
  border-radius: 10px;
  color: ${ColorPrimary};
  font-family: 'Nunito Sans';
  font-size: 28px;
  font-weight: 400;
  line-height: 42px;
  margin-bottom: 10px;
  margin-top: 24px;
  max-height: 350px;
  min-height: 250px;
  overflow: auto;
  padding: 2px 0;
  &::placeholder {
    color: ${ColorGray};
  }
  &:focus {
    outline: none;
  }
`;

const EntryTitle = styled('input')`
  border: none;
  border-radius: 10px;
  color: ${ColorPrimary};
  font-family: 'Nunito Sans';
  font-feature-settings: 'clig' off, 'liga' off;
  font-size: 19px;
  font-weight: 700;
  line-height: 28.5px;
  margin-top: 24px;
  width: 100%;
  &::placeholder {
    color: ${ColorGray};
  }
`;

const MediaBox = styled('button')`
  ${mediaBoxStyles}
  background-color: transparent;
  border: none;
`;

const MediaBoxDiv = styled('div')`
  ${mediaBoxStyles}
`;

const MediaContainer = styled('div')`
  margin-bottom: 24px;
  padding-top: 20px;
  width: 100%;
  .spinner-container {
    min-height: unset;
    width: 115px;
  }
`;

const MediaItems = styled('div')`
  align-items: center;
  display: flex;
  flex-direction: row;
  max-width: 100%;
  overflow-x: auto;
  padding-bottom: 12px;
  padding-top: 12px;
`;

const PrivacyEyeIconContainer = styled('div')`
  align-items: center;
  display: flex;
  height: 24px;
  justify-content: center;
  margin-right: 6px;
  width: 24px;
`;

const PrivacyRadioCheckContainer = styled('div')`
  margin-right: 6px;
  .MuiButtonBase-root {
    padding: 0;
  }
`;

const PrivacyToggleDiv = styled('div')`
  align-items: center;
  display: flex;
`;

const PrivacyToggleLabel = styled('span')`
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
`;

const JournalEntryModal = ({ onRequestClose, scrollToTop }) => {
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const {
    currentJournalEntry,
    currentAthlete,
    entryError,
    removeMediaArray,
    uploadMedia,
    uploadMediaExtended,
  } = useSelector((state) => state.journal.data);
  const { isLoadingEntry, isMediaLoading } = useSelector(
    (state) => state.journal.ui,
  );

  const dispatch = useDispatch();

  const isEditMode = !!currentJournalEntry?.id;
  const initialEntryData = isEditMode
    ? {
      title: currentJournalEntry.title || '',
      body: currentJournalEntry.body || '',
      media: currentJournalEntry.media || [],
    }
    : {
      title: '',
      body: '',
      media: [],
    };

  const [entryData, setEntryData] = useState(initialEntryData);
  const [entryVisible, setEntryVisible] = useState(
    isEditMode ? !currentJournalEntry.private : true,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const accountCode = currentUser?.accountCode;
  const admin = currentUser?.admin;
  const gradientColors = currentUser?.userSkin?.gradientColors;
  const currentUserName = [currentUser?.firstName || currentUser?.first,
    currentUser?.lastName || currentUser?.last].filter(Boolean).join(' ');
  const myJournal = currentAthlete?.id === currentUser?.id;

  let recipientId = null;
  if (myJournal) {
    recipientId = currentUser?.id;
  } else if (isEditMode) {
    recipientId = currentAthlete?.userId;
  } else {
    recipientId = currentAthlete?.id;
  }

  const displayDate = isEditMode
    ? currentJournalEntry?.createdAt
    : new Date().toLocaleString('default', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });

  const updateArrayToSubmit = Array.isArray(uploadMedia) && uploadMedia.length > 0
    ? uploadMedia.filter((mediaId) => !removeMediaArray.includes(mediaId))
    : [];

  const submitButtonDisabled = (!entryData.body.trim().length && !updateArrayToSubmit.length)
    || isSubmitting
    || isLoadingEntry;

  const handleCreateNewEntrySuccess = () => {
    if (scrollToTop) {
      scrollToTop();
    }
  };

  const handleChangeTitle = (e) => {
    const { value } = e.target;
    setEntryData((prev) => ({
      ...prev,
      title: value,
    }));
  };

  const handleChangeText = (e) => {
    const { value } = e.target;
    setEntryData((prev) => ({
      ...prev,
      body: value,
    }));
  };

  const handleDeleteMedia = (event) => {
    event.stopPropagation();
    const mediaId = event?.currentTarget?.id;
    dispatch(setRemoveMediaArray(mediaId));
  };

  const handleDeleteMediaKeyDown = (e, mediaId) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      dispatch(setRemoveMediaArray(mediaId));
    }
  };

  const handleOpenMessageVideo = (e, media) => {
    e.stopPropagation();
    dispatch(openMessageVideo(media.mediaUrl));
  };

  const handleOpenMessageVideoKeyDown = (e, media) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      handleOpenMessageVideo(e, media);
    }
  };

  const handleMediaBoxClick = (media) => {
    if (media.type === 'IMAGE' || media.type === 'EXTERNAL_URL') {
      dispatch(openImageLightbox(media.mediaUrl));
    }
  };

  const handlePrivacyToggle = () => {
    setEntryVisible((prev) => !prev);
  };

  const handleSubmit = () => {
    if (submitButtonDisabled) {
      return;
    }

    setIsSubmitting(true);

    const entryValues = {
      title: entryData.title,
      body: entryData.body,
      private: true,
      userId: recipientId,
      media: updateArrayToSubmit,
    };

    if (admin === 0 || (admin > 0 && !myJournal)) {
      entryValues.private = !entryVisible;
    }

    if (isEditMode) {
      dispatch(
        updateJournalEntry(accountCode, entryValues, currentJournalEntry.id),
      );
      journalTracker('Edit entry modal', `Journal entry updated ${myJournal ? '' : 'from coach to athlete'}`);
    } else {
      const isCreatingForMyJournal = myJournal || admin === 0;
      if (isCreatingForMyJournal) {
        dispatch(createMeJournalEntry(accountCode, entryValues, handleCreateNewEntrySuccess));
        journalTracker('Create entry modal', `Journal entry written to own journal - ${admin === 0 ? 'athlete' : 'coach'}`);
      } else {
        dispatch(createEntryToAthlete(accountCode, entryValues, handleCreateNewEntrySuccess));
        journalTracker('Create entry modal', 'Journal entry written from coach to athlete');
      }
    }
  };

  // Reset local submitting state when Redux loading state changes
  useEffect(() => {
    if (!isLoadingEntry) {
      setIsSubmitting(false);
    }
  }, [isLoadingEntry]);

  const renderMediaBox = (media) => {
    const backgroundImage = media?.thumbnailUrl ? getImageSource(media) : '';
    const isHidden = removeMediaArray.includes(media.id);
    const isVideo = media.type === 'VIDEO';
    const MediaWrapper = isVideo ? MediaBoxDiv : MediaBox;

    return (
      <MediaWrapper
        key={media.id}
        backgroundImage={backgroundImage}
        isHidden={isHidden}
        onClick={!isVideo ? () => handleMediaBoxClick(media) : undefined}
        aria-label={!isVideo ? 'View image' : undefined}
      >
        <IcomoonReact
          id={media.id}
          role='button'
          tabIndex={0}
          onClick={handleDeleteMedia}
          onKeyDown={(e) => handleDeleteMediaKeyDown(e, media.id)}
          aria-label='Remove media'
          className='remove-media'
          iconSet={iconSet}
          size={20}
          icon='remove'
          color={ColorGray}
        />
        {isVideo && (
          <IcomoonReact
            role='button'
            tabIndex={0}
            onClick={(e) => handleOpenMessageVideo(e, media)}
            onKeyDown={(e) => handleOpenMessageVideoKeyDown(e, media)}
            aria-label='Play video'
            className='play-icon'
            iconSet={iconSet}
            size={40}
            icon='play-filled'
            color={ColorWhite}
          />
        )}
      </MediaWrapper>
    );
  };

  const renderMedia = () => {
    if (updateArrayToSubmit.length > 0 || isMediaLoading) {
      return (
        <MediaContainer>
          {(uploadMediaExtended.length > 0 || isMediaLoading) && (
            <MediaItems>
              {uploadMediaExtended.map(renderMediaBox)}
              {isMediaLoading && <Spinner large={false} aria-label='Loading media...' />}
            </MediaItems>
          )}
        </MediaContainer>
      );
    }
    return null;
  };

  const renderEntryBody = () => (
    <>
      <EntryTitle
        placeholder='Entry title here...'
        aria-label='Type entry title here'
        value={entryData.title}
        onChange={handleChangeTitle}
        maxLength={255}
      />
      <EntryText
        placeholder='Type your journal entry here...'
        aria-label='Type entry text here'
        value={entryData.body}
        onChange={handleChangeText}
        rows={3}
        maxLength={20000}
      />
      {renderMedia()}
    </>
  );

  const renderErrorMessage = () => {
    const errorMessage = entryError?.message || entryError?.response?.headers?.message;
    if (errorMessage) {
      return <EntryErrorText>{errorMessage}</EntryErrorText>;
    }
    return null;
  };

  const renderPrivacyToggleContent = () => {
    if (admin > 0 && myJournal) {
      return (
        <>
          <PrivacyEyeIconContainer>
            <IcomoonReact
              aria-hidden='true'
              iconSet={iconSet}
              size={22}
              icon='invisible'
              color='gray'
            />
          </PrivacyEyeIconContainer>
          This entry is private
        </>
      );
    }

    const toggleText = admin > 0 ? 'Allow athlete to view' : 'Allow coach to view';
    const checkboxId = 'privacy-checkbox';
    return (
      <>
        <PrivacyRadioCheckContainer>
          <Checkbox
            id={checkboxId}
            checked={entryVisible}
            onChange={handlePrivacyToggle}
            color='primary'
            className='privacy-checkbox'
            aria-labelledby={`${checkboxId}-label`}
          />
        </PrivacyRadioCheckContainer>
        <PrivacyToggleLabel id={`${checkboxId}-label`}>{toggleText}</PrivacyToggleLabel>
      </>
    );
  };

  const renderPostActions = () => (
    <>
      <AttachmentButton />
      <PrivacyToggleDiv>{renderPrivacyToggleContent()}</PrivacyToggleDiv>
    </>
  );

  return (
    <CreateEditEntryModal
      currentUserName={currentUserName}
      displayDate={displayDate}
      gradientColors={gradientColors}
      isEditMode={isEditMode}
      onClose={onRequestClose}
      onSubmit={handleSubmit}
      postActions={renderPostActions()}
      submitDisabled={submitButtonDisabled}
      userPic={currentUser?.pic}
    >
      {renderEntryBody()}
      {renderErrorMessage()}
    </CreateEditEntryModal>
  );
};

JournalEntryModal.propTypes = {
  onRequestClose: PropTypes.func.isRequired,
  scrollToTop: PropTypes.func,
};

JournalEntryModal.defaultProps = {
  scrollToTop: null,
};

export default JournalEntryModal;
